// src/pages/NewWorkflowPage.tsx
// Author: CorestratAI Solutions
// Workflow page — multi-step autonomous tasks (scraping, data collection, file generation)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { workflowApi } from '../services/api';
import { Message } from '../types/chat';
import ThemeToggle from '../components/ThemeToggle';
import { trackWorkflowStarted } from '../utils/analytics';
import CodeDisplay from '../components/CodeDisplay';
import Sidebar from '../components/Sidebar';
import ThinkingSteps from '../components/ThinkingSteps';
import ExecutionStages from '../components/ExecutionStages';
import WorkflowMessage from '../components/WorkflowMessage';
import DocumentationButton from '../components/Documentation/DocumentationButton';
import DocumentationModal from '../components/Documentation/DocumentationModal';
import { useWorkflowStore } from '../stores/workflowStore';

const NewWorkflowPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams<{ sessionId: string }>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { logout, user } = useAuth();

  // Zustand store — state survives tab switches
  const {
    messages, setMessages,
    input, setInput,
    loading, setLoading,
    currentSessionId, setCurrentSessionId,
    workspaceModalOpen, setWorkspaceModalOpen,
    workspaceContent, setWorkspaceContent,
    thinkingSteps, setThinkingSteps,
    executionStages, setExecutionStages,
    isStreamingActive, setIsStreamingActive,
    statusMessage, setStatusMessage,
    statusProgress, setStatusProgress,
    charts, setCharts, addChart,
    workflowDetails, setWorkflowDetails,
  } = useWorkflowStore();

  // Local state — ephemeral
  const [convertingToPdf, setConvertingToPdf] = useState<string | null>(null);
  const [showChartCode, setShowChartCode] = useState(false);
  const [documentationOpen, setDocumentationOpen] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);

  // Auto-scroll: when streaming completes, scroll to bottom to show full response
  useEffect(() => {
    if (!isStreamingActive && !loading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isStreamingActive, loading]);

  // ChatGPT-style: when user sends a message, scroll so their message is near the top
  useEffect(() => {
    if (isStreamingActive && lastUserMessageRef.current) {
      const container = messagesContainerRef.current;
      if (container) {
        const userMsgEl = lastUserMessageRef.current;
        const containerRect = container.getBoundingClientRect();
        const msgRect = userMsgEl.getBoundingClientRect();
        const offsetFromTop = msgRect.top - containerRect.top + container.scrollTop;
        container.scrollTo({ top: offsetFromTop - 60, behavior: 'smooth' });
      }
    }
  }, [isStreamingActive]);

  // Pre-fill prompt from URL query param (e.g. /workflow?prompt=...).
  // If &autosubmit=1 is also present (concierge "yes" flow), stash the prompt
  // and let a separate effect submit it once currentSessionId is ready (session
  // creation is async — if we submit immediately, the session might not exist
  // yet and handleSendMessage early-returns).
  const [pendingAutoSubmit, setPendingAutoSubmit] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const promptParam = params.get('prompt');
    const autosubmit = params.get('autosubmit') === '1';
    if (promptParam) {
      setInput(promptParam);
      window.history.replaceState({}, '', window.location.pathname);
      if (autosubmit) {
        console.log('[WORKFLOW] autosubmit=1 detected, queued for after session-ready:', promptParam);
        setPendingAutoSubmit(promptParam);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire auto-submit once session is established and we're not already loading.
  useEffect(() => {
    if (pendingAutoSubmit && currentSessionId && !loading) {
      console.log('[WORKFLOW] autosubmit firing:', pendingAutoSubmit);
      const promptToSend = pendingAutoSubmit;
      setPendingAutoSubmit(null);
      handleSendMessage(null, promptToSend);
    }
  }, [pendingAutoSubmit, currentSessionId, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Monitor token availability with polling
  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('auth_token');
      const tokenAvailable = !!token;

      if (tokenAvailable && !hasToken) {
        console.log('[WORKFLOW] Token became available');
        setHasToken(true);
      } else if (!tokenAvailable && hasToken) {
        console.log('[WORKFLOW] Token removed');
        setHasToken(false);
      }
    };

    checkToken();

    const tokenCheckInterval = setInterval(checkToken, 500);
    const stopCheckTimeout = setTimeout(() => {
      clearInterval(tokenCheckInterval);
    }, 5000);

    return () => {
      clearInterval(tokenCheckInterval);
      clearTimeout(stopCheckTimeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // URL-based session initialization
  useEffect(() => {
    const initSession = async () => {
      if (urlSessionId) {
        localStorage.setItem('lastWorkflowSession', urlSessionId);
        setCurrentSessionId(urlSessionId);
        if (messages.length === 0) {
          try {
            const data = await workflowApi.getSessionMessages(urlSessionId);
            const loaded = data.messages.map((msg: any) => ({
              id: msg.id || msg.message_id || Date.now().toString(),
              type: msg.role === 'user' ? 'user' : 'system',
              content: msg.content,
              timestamp: new Date(msg.timestamp),
            }));
            setMessages(loaded);
          } catch (error) {
            console.error('[WORKFLOW] Failed to load session messages:', error);
            localStorage.removeItem('lastWorkflowSession');
            navigate('/workflow', { replace: true });
          }
        }
        return;
      }
      // No session ID in URL - check if there's a recent session to restore
      const lastSession = localStorage.getItem('lastWorkflowSession');
      if (lastSession) {
        console.log('[WORKFLOW] Restoring last session:', lastSession);
        navigate(`/workflow/${lastSession}`, { replace: true });
        return;
      }
      // No previous session - create new one
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('auth_token');
      if (!token) return;
      try {
        const session = await workflowApi.startSession();
        navigate(`/workflow/${session.session_id}`, { replace: true });
      } catch (error) {
        console.error('[WORKFLOW] Failed to create session:', error);
      }
    };
    initSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSessionId]);

  // Token-availability fallback: create session once token arrives
  useEffect(() => {
    if (hasToken && !urlSessionId && !currentSessionId) {
      const lastSession = localStorage.getItem('lastWorkflowSession');
      if (lastSession) {
        navigate(`/workflow/${lastSession}`, { replace: true });
        return;
      }
      (async () => {
        try {
          const session = await workflowApi.startSession();
          navigate(`/workflow/${session.session_id}`, { replace: true });
        } catch (error) {
          console.error('[WORKFLOW] Failed to create session after token:', error);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToken]);

  // Start new session
  const startNewSession = useCallback(async () => {
    const token = localStorage.getItem('access_token') || sessionStorage.getItem('auth_token');
    if (!token) return;
    try {
      localStorage.removeItem('lastWorkflowSession');
      const session = await workflowApi.startSession();
      setMessages([]);
      navigate(`/workflow/${session.session_id}`, { replace: true });
    } catch (error) {
      console.error('Failed to start workflow session:', error);
    }
  }, [navigate]);

  const handleSendMessage = async (e?: React.FormEvent | null, overrideText?: string) => {
    if (e && typeof (e as React.FormEvent).preventDefault === 'function') {
      (e as React.FormEvent).preventDefault();
    }

    // overrideText is used by the concierge auto-submit path (URL ?autosubmit=1)
    // when the student already said yes in chat — we don't want to require a
    // second click here. Falls back to the current input field for normal submits.
    const messageText = (overrideText ?? input).trim();
    if (!messageText || loading || !currentSessionId) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setIsStreamingActive(true);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = '40px';
    }

    const systemMessageId = (Date.now() + 1).toString();
    const systemMessage: Message = {
      id: systemMessageId,
      type: 'system',
      content: '',
      timestamp: new Date(),
    };

    // Initialize thinking steps and clear previous charts/details
    setThinkingSteps([]);
    setCharts([]);
    setWorkflowDetails(null);

    let streamedContent = '';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let hasCodeBlocks = false;
    let backendIntent = '';
    let pendingFiles: any[] = [];  // Track files from onComplete

    // Add system message immediately so ThinkingSteps can render during tool execution
    setMessages(prev => [...prev, { ...systemMessage, content: '', intent: 'workflow' }]);

    try {
      trackWorkflowStarted(messageText);
      await workflowApi.sendMessageStream(
        currentSessionId,
        { message: messageText },
        (chunk: string) => {
          streamedContent += chunk;
          setLoading(false);

          setMessages(prev => prev.map(msg =>
            msg.id === systemMessageId
              ? { ...msg, content: streamedContent, intent: backendIntent || 'workflow', ...(pendingFiles.length > 0 ? { generatedFiles: pendingFiles } : {}) }
              : msg
          ));
        },
        (codeBlocks: any[], isDashboard?: boolean, intent?: string, files?: any[]) => {
          console.log('[WORKFLOW] Received code blocks:', codeBlocks);
          console.log('[WORKFLOW] Backend intent:', intent);
          console.log('[WORKFLOW] Generated files:', files);

          // Store generated files in message
          if (files && files.length > 0) {
            console.log('[WORKFLOW] Storing generated files in message:', files);
            pendingFiles = files;  // Store for onChunk to pick up
            setMessages(prev => prev.map(msg =>
              msg.id === systemMessageId
                ? { ...msg, generatedFiles: files, content: streamedContent, intent: backendIntent }
                : msg
            ));
          }

          if (codeBlocks && codeBlocks.length > 0) {
            hasCodeBlocks = true;
            backendIntent = intent || '';
          }

          // Update message with intent (is_dashboard is always false for workflows)
          if (intent) {
            backendIntent = intent;
            setMessages(prev => prev.map(msg =>
              msg.id === systemMessageId
                ? { ...msg, intent: intent, is_dashboard: false }
                : msg
            ));
            console.log('[WORKFLOW] Updated message with intent:', intent);
          }
        },
        (message: string, step: string, progress: number) => {
          console.log('[WORKFLOW] Status update:', message, 'Step:', step, 'Progress:', progress);
          setStatusMessage(message);
          setStatusProgress(progress);

          // Update execution stages for code execution flow
          if (step === 'preparation' || step === 'executing') {
            setExecutionStages(prev => {
              if (prev.length === 0) {
                return [
                  { id: 'analyzing', label: 'Analyzing your request', status: 'completed' },
                  { id: 'generating', label: 'Generating code', status: 'completed' },
                  { id: 'preparing', label: 'Preparing execution environment', status: step === 'preparation' ? 'in_progress' : 'completed' },
                  { id: 'executing', label: 'Executing code', status: step === 'executing' ? 'in_progress' : 'pending' },
                ];
              }

              return prev.map(stage => {
                if (stage.id === 'preparing' && step === 'preparation') {
                  return { ...stage, status: 'in_progress' };
                }
                if (stage.id === 'executing' && step === 'executing') {
                  return { ...stage, status: 'in_progress' };
                }
                if (stage.id === 'preparing' && step === 'executing') {
                  return { ...stage, status: 'completed' };
                }
                return stage;
              });
            });
          }

          // Create or update thinking step — mark previous steps as completed
          if (step && message) {
            setThinkingSteps(prev => {
              const existingStepIndex = prev.findIndex(s => s.id === step);

              if (existingStepIndex !== -1) {
                const updated = [...prev];
                updated[existingStepIndex] = {
                  ...updated[existingStepIndex],
                  title: message,
                  progress: progress,
                  status: progress >= 100 ? 'completed' : 'in_progress',
                };
                return updated;
              } else {
                // Mark all previous in_progress steps as completed
                const updated = prev.map(s =>
                  s.status === 'in_progress' ? { ...s, status: 'completed' as const } : s
                );
                return [...updated, {
                  id: step,
                  title: message,
                  status: progress >= 100 ? 'completed' as const : 'in_progress' as const,
                  timestamp: new Date(),
                  progress: progress,
                  isExpandable: false,
                }];
              }
            });
          }
        },
        (chart: any) => {
          console.log('[WORKFLOW] onVizComplete called:', chart.title, chart.title === '__workflow_details__' ? 'DETAILS' : 'CHART');
          // Handle workflow_details metadata from workflow_complete
          if (chart.title === '__workflow_details__') {
            console.log('[WORKFLOW] Setting workflow details:', {
              toolsUsed: chart.tools_used,
              sourceUrl: chart.source_url,
              chartCode: chart.chart_code ? `${chart.chart_code.length} chars` : 'none',
            });
            setWorkflowDetails({
              toolsUsed: chart.tools_used || [],
              sourceUrl: chart.source_url || '',
              chartCode: chart.chart_code || '',
              elapsedSeconds: chart.elapsed_seconds || 0,
            });
            return;
          }
          // viz_complete — structured chart data from backend
          addChart(chart);
        }
      );

      // Final message update — ensure generatedFiles and intent are set
      if (pendingFiles.length > 0 || backendIntent) {
        setMessages(prev => prev.map(msg =>
          msg.id === systemMessageId
            ? { ...msg, content: streamedContent, intent: backendIntent || 'workflow', ...(pendingFiles.length > 0 ? { generatedFiles: pendingFiles } : {}) }
            : msg
        ));
      }
      console.log('[WORKFLOW] Message received. User can click Execute Code or Download buttons.');
    } catch (error: any) {
      console.error('Error sending workflow message:', error);
      const errorMsg = error?.message || '';
      const is402 = errorMsg.includes('Insufficient credits') || errorMsg.includes('PAYMENT_REQUIRED') || errorMsg.includes('trial has ended') || errorMsg.includes('Upgrade') || errorMsg.includes('requires Pro');
      // v3.19.7.1: surface backend's actual 402 detail rather than v3.18 hardcoded copy.
      const errorContent = is402
        ? (errorMsg.replace(/^PAYMENT_REQUIRED:?\s*/i, '') || 'Workflow needs Pro or Ultimate. Upgrade to continue.')
        : 'Sorry, there was an error processing your workflow request.';
      setMessages(prev => prev.map(msg =>
        msg.id === systemMessageId
          ? { ...msg, content: errorContent }
          : msg
      ));
      if (is402) {
        const reason = errorMsg.toLowerCase().includes('pro or ultimate') ? 'workflow_requires_pro' : 'trial_expired';
        setTimeout(() => navigate(`/pay?reason=${reason}`), 2500);
      }
    } finally {
      setLoading(false);
      setIsStreamingActive(false);
      setStatusMessage('');
      setStatusProgress(0);
      setThinkingSteps(prev => prev.map(step => ({
        ...step,
        status: step.status === 'in_progress' ? 'completed' : step.status
      })));
      setExecutionStages(prev => prev.map(stage => ({
        ...stage,
        status: 'completed'
      })));
      setTimeout(() => {
        setExecutionStages([]);
      }, 2000);
    }
  };

  const handleConvertToPdf = async (messageContent: string, messageId: string) => {
    setConvertingToPdf(messageId);
    try {
      let cleanedContent = messageContent;

      const firstHeadingIndex = cleanedContent.search(/^#+\s+/m);
      if (firstHeadingIndex !== -1) {
        cleanedContent = cleanedContent.substring(firstHeadingIndex);
      }

      const titleMatch = cleanedContent.match(/^#{1,3}\s+(.+?)$/m);
      const documentTitle = titleMatch ? titleMatch[1] : 'Workflow Document';

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/convert-to-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          markdown: cleanedContent.trim(),
          title: documentTitle,
          filename: `${documentTitle.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_${new Date().toISOString().split('T')[0]}`
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to convert to PDF: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.pdf_data) {
        const linkSource = `data:application/pdf;base64,${result.pdf_data}`;
        const downloadLink = document.createElement('a');
        downloadLink.href = linkSource;
        downloadLink.download = result.pdf_filename || 'document.pdf';
        downloadLink.click();
      } else {
        throw new Error(result.error || 'PDF generation failed');
      }
    } catch (err: any) {
      console.error('Failed to convert to PDF:', err);
      alert(`Failed to generate PDF: ${err.message}`);
    } finally {
      setConvertingToPdf(null);
    }
  };

  const renderMessageContent = (content: string) => {
    // Strip any base64 image data that leaked into content (charts are rendered separately now)
    content = content
      .replace(/<!--CHART:[\s\S]*?-->/g, '')
      .replace(/!\[.*?\]\(data:image\/[^)]+\)/g, '')
      .replace(/\(data:image\/[^\s)]+\)/g, '')
      .replace(/data:image\/png;base64,[A-Za-z0-9+/=]{100,}/g, '')
      .replace(/\n{3,}/g, '\n\n');

    // Split out chart markers first
    const chartPattern = /(<!--CHART:[\s\S]*?-->)/;
    const completeBlockPattern = /(```[\s\S]*?```)/;

    // Combined split: charts and code blocks
    const combinedPattern = /(<!--CHART:[\s\S]*?-->|```[\s\S]*?```)/;
    const parts = content.split(combinedPattern);

    return parts.map((part, index) => {
      // Handle chart images
      if (part.startsWith('<!--CHART:') && part.endsWith('-->')) {
        const inner = part.slice(10, -3); // Remove <!--CHART: and -->
        const firstColon = inner.indexOf(':');
        const title = inner.substring(0, firstColon);
        const base64Data = inner.substring(firstColon + 1);
        return (
          <div key={index} className="my-4 rounded-lg overflow-hidden border border-border">
            <div className="px-4 py-2 bg-muted/50 border-b border-border">
              <span className="text-sm font-semibold text-primary">{title}</span>
            </div>
            <img
              src={`data:image/png;base64,${base64Data}`}
              alt={title}
              className="w-full max-w-2xl mx-auto block"
            />
          </div>
        );
      }

      // Handle COMPLETE code blocks
      if (part.startsWith('```') && part.endsWith('```')) {
        const firstLineEnd = part.indexOf('\n');
        const language = part.substring(3, firstLineEnd).trim();
        const code = part.substring(firstLineEnd + 1, part.length - 3).trim();

        return (
          <div key={index} className="my-4 bg-card border border-border rounded-lg shadow-md w-full overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border">
              <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-mono font-semibold uppercase rounded border border-primary/20">
                {language || 'code'}
              </span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(code);
                }}
                className="flex items-center space-x-1 px-3 py-1.5 bg-muted hover:bg-muted/80 text-foreground text-xs font-medium rounded transition-colors"
                title="Copy code"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>Copy</span>
              </button>
            </div>
            <div className="overflow-x-auto">
              <pre className="p-4 text-sm font-mono text-foreground bg-gradient-to-br from-background to-muted/20 w-full">
                <code className="block">{code}</code>
              </pre>
            </div>
          </div>
        );
      }
      // Handle INCOMPLETE/STREAMING code blocks
      else if (part.startsWith('```') && !part.endsWith('```')) {
        const firstLineEnd = part.indexOf('\n');
        if (firstLineEnd > 0) {
          const language = part.substring(3, firstLineEnd).trim();
          const code = part.substring(firstLineEnd + 1).trim();

          return (
            <div key={index} className="my-4 bg-card border border-border rounded-lg shadow-md w-full overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border">
                <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-mono font-semibold uppercase rounded border border-primary/20">
                  {language || 'code'}
                </span>
                <span className="flex items-center space-x-1 text-xs text-muted-foreground">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span>Streaming...</span>
                </span>
              </div>
              <div className="overflow-x-auto">
                <pre className="p-4 text-sm font-mono text-foreground bg-gradient-to-br from-background to-muted/20 w-full">
                  <code className="block">{code}</code>
                </pre>
              </div>
            </div>
          );
        }
      }
      // Regular text content
      {
        const lines = part.split('\n');
        let inTable = false;
        let tableRows: string[] = [];
        let formattedContent: string[] = [];

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();

          if (line.includes('|') && line.split('|').length > 2) {
            if (!inTable) {
              inTable = true;
              tableRows = [];
            }
            tableRows.push(line);
          } else {
            if (inTable && tableRows.length > 0) {
              formattedContent.push(renderTable(tableRows));
              tableRows = [];
              inTable = false;
            }

            if (line) {
              formattedContent.push(formatLine(line));
            } else {
              formattedContent.push('<div class="h-3"></div>');
            }
          }
        }

        if (inTable && tableRows.length > 0) {
          formattedContent.push(renderTable(tableRows));
        }

        return (
          <div
            key={index}
            className="text-[15px] leading-relaxed space-y-1"
            dangerouslySetInnerHTML={{ __html: formattedContent.join('') }}
          />
        );
      }
    });
  };

  const formatLine = (line: string): string => {
    // Image line — render as block image
    if (line.trim().startsWith('![') && line.includes('data:image/')) {
      return '<div class="my-4">' + cleanInlineMarkdown(line) + '</div>';
    }
    if (line.startsWith('### ')) {
      const text = line.substring(4).trim();
      return '<h3 class="text-base font-semibold mt-4 mb-2 text-primary">' + cleanInlineMarkdown(text) + '</h3>';
    } else if (line.startsWith('## ')) {
      const text = line.substring(3).trim();
      return '<h2 class="text-lg font-bold mt-5 mb-2 text-primary">' + cleanInlineMarkdown(text) + '</h2>';
    } else if (line.startsWith('# ') && !line.startsWith('##')) {
      const text = line.substring(2).trim();
      return '<h1 class="text-xl font-bold mt-5 mb-3 text-primary">' + cleanInlineMarkdown(text) + '</h1>';
    }

    if (line.startsWith('---') || line.startsWith('***')) {
      return '<hr class="my-4 border-border" />';
    }

    const trimmedLine = line.trim();
    const hasListMarker = /^[•\-*\d+.]\s/.test(trimmedLine);

    if (hasListMarker) {
      return '<li class="ml-4 mb-1.5">' + cleanInlineMarkdown(trimmedLine) + '</li>';
    }

    if (line.startsWith('- ') || line.startsWith('* ')) {
      const listContent = cleanInlineMarkdown(line.substring(2).trim());
      return '<li class="ml-4 mb-1.5">' + listContent + '</li>';
    }

    if (line.trim() === '') {
      return '<div class="h-3"></div>';
    }

    return '<span class="block mb-2">' + cleanInlineMarkdown(line) + '</span>';
  };

  const cleanInlineMarkdown = (text: string): string => {
    // Handle inline images (base64 charts from viz agent) BEFORE escaping HTML
    // Store image tags, replace after escaping
    const imageStore: string[] = [];
    text = text.replace(
      /!\[([^\]]*)\]\((data:image\/[^)]+)\)/g,
      (_match, alt, src) => {
        const placeholder = `__IMG_${imageStore.length}__`;
        imageStore.push(`<img src="${src}" alt="${alt}" class="max-w-full rounded-lg my-4 border border-border" />`);
        return placeholder;
      }
    );
    text = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    text = text.replace(/###\s*/g, '');
    text = text.replace(/##\s*/g, '');
    text = text.replace(/#\s+/g, '');
    text = text.replace(/\*\*([^*]+?)\*\*/g, '<strong class="font-semibold">$1</strong>');
    text = text.replace(/\*([^*]+?)\*/g, '<em class="italic">$1</em>');
    text = text.replace(/_([^_]+?)_/g, '<em class="italic">$1</em>');
    text = text.replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 bg-muted text-foreground rounded text-xs font-mono">$1</code>');
    text = text.replace(
      /(https?:\/\/[^\s<]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline">$1</a>'
    );
    text = text.replace(/\|/g, ' ');
    text = text.replace(/---+/g, '');
    text = text.replace(/___+/g, '');
    text = text.replace(/\*\*\*+/g, '');
    text = text.replace(/\s{2,}/g, ' ');
    text = text.trim();

    // Restore image tags from placeholders
    imageStore.forEach((imgTag, i) => {
      text = text.replace(`__IMG_${i}__`, imgTag);
    });

    return text;
  };

  const renderTable = (rows: string[]): string => {
    if (rows.length < 2) return rows.join('<br />');

    let html = '<table class="my-4 min-w-full border-collapse border border-border rounded-lg overflow-hidden"><tbody>';

    rows.forEach((row, idx) => {
      if (row.includes('---')) return;

      const cells = row.split('|').filter(cell => cell.trim());
      const isHeader = idx === 0;

      html += '<tr class="' + (isHeader ? 'bg-muted font-semibold' : 'even:bg-muted/30') + '">';
      cells.forEach(cell => {
        const tag = isHeader ? 'th' : 'td';
        html += `<${tag} class="border border-border px-4 py-2 text-left">${cell.trim()}</${tag}>`;
      });
      html += '</tr>';
    });

    html += '</tbody></table>';
    return html;
  };

  // Handler to open workspace modal with code for execution
  const handleExecuteCode = (messageContent: string, messageId: string) => {
    console.log('[WORKFLOW] Opening workspace modal for code execution');

    const codeBlockPattern = /```(\w+)?\s*\n(.*?)```/gs;
    const matches = Array.from(messageContent.matchAll(codeBlockPattern));

    const codeBlocks = matches.map(match => ({
      language: match[1] || 'python',
      code: match[2]
    }));

    console.log('[WORKFLOW] Extracted code blocks for execution:', codeBlocks.length);

    setWorkspaceContent({
      type: 'code',
      codeBlocks,
      messageId
    });
    setWorkspaceModalOpen(true);
  };

  const examplePrompts = [
    'Search for top 50 Python beginner projects, scrape data, create CSV',
    'Find scholarship opportunities for CS students, compile into spreadsheet',
    'Research popular study apps, compare features, create comparison chart',
    'Scrape job listings from 3 sites and merge into one dataset',
  ];

  return (
    <div className="flex flex-col h-screen bg-background text-foreground overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4">
            <img
              src="/cubemate-logo.png"
              alt="Cubemate"
              className="w-10 h-10 rounded-lg"
            />
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-xl font-bold bg-gradient-to-r from-primary to-brand bg-clip-text text-transparent">
                  Workflow
                </h1>
              </div>
              <p className="text-xs text-muted-foreground/80 font-normal italic">
                Multi-step autonomous tasks — scrape, collect, generate
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={startNewSession}
              className="px-4 py-2 text-sm bg-gradient-to-r from-[#8E3BCE] to-[#6D2D8D] text-white hover:shadow-lg hover:shadow-primary/30 rounded-lg transition-all duration-200 font-semibold flex items-center space-x-1.5"
              title="Start a new workflow session"
            >
              <span>+ New</span>
            </button>
            <span className="hidden md:inline-flex"><DocumentationButton onClick={() => setDocumentationOpen(true)} /></span>
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Help modal */}
      <DocumentationModal
        isOpen={documentationOpen}
        onClose={() => setDocumentationOpen(false)}
        onPromptClick={(prompt: string) => {
          setDocumentationOpen(false);
          setInput(prompt);
        }}
        currentRoute="flowmate"
      />

      {/* Body: Sidebar + Main Content */}
      <div className="flex flex-1 overflow-hidden bg-gradient-to-br from-background via-background to-primary/5">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-primary/10 via-transparent to-transparent rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-brand/10 via-transparent to-transparent rounded-full blur-3xl animate-pulse-slower"></div>
        </div>

        {/* Sidebar */}
        <Sidebar />

        {/* Chat Container - Full width */}
        <div className="flex-1 flex flex-col overflow-hidden relative">

          {messages.length === 0 ? (
            /* Empty State - Centered Layout */
            <div className="flex-1 flex flex-col items-center justify-center px-4 pb-16">
              <div className="text-center space-y-6 max-w-2xl mb-8 animate-fade-in">
                <h2 className="text-3xl md:text-4xl font-semibold text-foreground leading-tight">
                  Automate your research. Get results.
                </h2>
                <p className="text-xl md:text-2xl text-muted-foreground italic">
                  What data do you need collected today?
                </p>
              </div>

              {/* Input Form - Centered */}
              <div className="w-full max-w-3xl relative z-10">
                <form onSubmit={handleSendMessage} className="relative">
                  <div className="relative w-full">
                    <div className="relative flex items-center bg-white dark:bg-card border border-border/30 rounded-full p-3 shadow-xl hover:shadow-2xl transition-all">
                      <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (!loading && input.trim() && currentSessionId) {
                              handleSendMessage(e as any);
                            }
                          }
                        }}
                        placeholder="Try: 'Download Iris dataset and create a scatter plot colored by species'"
                        disabled={loading}
                        rows={1}
                        className="flex-1 px-4 py-3 bg-transparent text-foreground placeholder-muted-foreground/60 focus:outline-none resize-none min-h-[40px] max-h-[120px] overflow-y-auto text-[15px]"
                      />
                      <button
                        type="submit"
                        disabled={loading || !input.trim() || !currentSessionId}
                        className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-[#8E3BCE] to-[#6D2D8D] text-white rounded-full hover:shadow-lg hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                      >
                        {loading ? (
                          <span className="text-lg">&#8987;</span>
                        ) : (
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
                {/* Example Prompts */}
                <div className="mt-6 w-full animate-fade-in">
                  <p className="text-sm text-muted-foreground text-center mb-3 font-medium">
                    Example Prompts
                  </p>
                  <div className="divide-y divide-border/30">
                    {examplePrompts.map((prompt, idx) => (
                      <button
                        key={idx}
                        onClick={() => setInput(prompt)}
                        className="w-full py-3 text-sm text-foreground hover:text-primary text-center transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* With Messages - Scrollable Layout */
            <div className="flex-1 w-full max-w-[90%] mx-auto flex flex-col overflow-hidden">
              {/* Messages - Scrollable area */}
              <div ref={messagesContainerRef} className="flex-1 overflow-y-auto space-y-6 py-6 px-4 relative z-10">

            {(() => { const lastUserIdx = messages.reduce((acc, m, i) => m.type === 'user' ? i : acc, -1); return messages.filter((message, index) => {
              // Hide empty system messages that have no content and no thinking steps to show
              if (message.type === 'system' && !(message.content || '').trim() && index === messages.length - 1 && thinkingSteps.length === 0 && executionStages.length === 0) return false;
              return true;
            }).map((message, index) => (
              <div
                key={message.id}
                ref={message.type === 'user' && index === lastUserIdx ? lastUserMessageRef : undefined}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in-up`}>
                <div className={`${message.type === 'user' ? 'max-w-2xl' : 'max-w-7xl w-full'} rounded-2xl p-5 backdrop-blur-sm ${
                  message.type === 'user'
                    ? 'bg-primary text-primary-foreground border border-primary/20'
                    : 'bg-card/80 border border-border/50'
                } transition-all duration-200`}>
                  <div className="flex items-start space-x-3">
                    {message.type === 'user' ? (
                      <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-base bg-primary-foreground/20 border border-primary-foreground/30">
                        &#128104;&#8205;&#128187;
                      </div>
                    ) : (
                      <img
                        src="/cubemate-logo.png"
                        alt="Cubemate"
                        className="flex-shrink-0 w-9 h-9 rounded-lg"
                      />
                    )}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      {/* Execution stages - shown ABOVE content for the last message being streamed */}
                      {message.type === 'system' &&
                       message.id === messages[messages.length - 1]?.id &&
                       executionStages.length > 0 && (
                        <div className="mb-4">
                          <ExecutionStages stages={executionStages} />
                        </div>
                      )}

                      {/* Thinking steps - shown ABOVE content for assistant messages */}
                      {message.type === 'system' &&
                       message.id === messages[messages.length - 1]?.id &&
                       thinkingSteps.length > 0 && (
                        <div className="mb-4">
                          <ThinkingSteps steps={thinkingSteps} />
                        </div>
                      )}

                      <div className="w-full">
                        {message.intent === 'workflow' ? (
                          <WorkflowMessage
                            content={message.content}
                            generatedFiles={(message as any).generatedFiles}
                          />
                        ) : (
                          renderMessageContent(message.content)
                        )}
                      </div>

                      {/* Action buttons */}
                      {message.type !== 'user' && (
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          {/* Execute Code Button */}
                          {(() => {
                            const hasActualCode = /```/i.test(message.content);
                            return ['code', 'code_execution', 'execution'].includes(message.intent || '') && hasActualCode && (
                              <button
                                onClick={() => handleExecuteCode(message.content, message.id)}
                                className="px-3 py-1.5 text-xs bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-lg transition-all flex items-center space-x-1.5 shadow-sm font-medium hover:scale-105"
                                title="Execute this code in workspace"
                              >
                                <span>&#9654;&#65039;</span>
                                <span>Execute Code</span>
                              </button>
                            );
                          })()}

                          {/* Download handled by WorkflowMessage compact bar — no duplicate button */}

                          {/* Export PDF Button - for research/document intent */}
                          {['research', 'document'].includes(message.intent || '') && message.content.length > 300 && (
                            <button
                              onClick={() => handleConvertToPdf(message.content, message.id)}
                              disabled={convertingToPdf === message.id}
                              className="px-3 py-1.5 text-xs bg-green-500/10 hover:bg-green-500/20 text-green-600 dark:text-green-400 rounded-lg transition-all disabled:opacity-50 flex items-center space-x-1.5 shadow-sm font-medium hover:scale-105"
                              title="Export this response as PDF"
                            >
                              {convertingToPdf === message.id ? (
                                <>
                                  <span className="animate-spin">&#8987;</span>
                                  <span>Converting...</span>
                                </>
                              ) : (
                                <>
                                  <span>&#128196;</span>
                                  <span>Export PDF</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )); })()}

            {/* Charts — rendered as structured components, not inline content */}
            {!loading && charts.length > 0 && (
              <div className="space-y-4 mb-4">
                {charts.map((chart, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm animate-fade-in-up">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-muted/50 border-b border-border/50">
                      <span className="text-sm font-semibold text-foreground">{chart.title}</span>
                      <span className="text-xs text-muted-foreground px-2 py-0.5 bg-primary/10 rounded border border-primary/20">
                        {chart.chart_type}
                      </span>
                    </div>
                    <div className="p-2">
                      <img
                        src={`data:image/png;base64,${chart.data}`}
                        alt={chart.title}
                        className="w-full rounded-lg"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Workflow Details — tools used, source URL, chart code */}
            {!loading && workflowDetails && (workflowDetails.toolsUsed.length > 0 || workflowDetails.sourceUrl || workflowDetails.chartCode) && (
              <div className="mb-4 rounded-xl border border-border/50 bg-card/80 backdrop-blur-sm p-4 text-sm animate-fade-in-up">
                <div className="font-semibold text-foreground mb-3">Workflow Details</div>

                {workflowDetails.toolsUsed.length > 0 && (
                  <div className="mb-2">
                    <span className="text-muted-foreground text-xs">Steps: </span>
                    <span className="text-foreground/80">
                      {workflowDetails.toolsUsed.map(t => {
                        const labels: Record<string, string> = {
                          web_search: 'Search', scrape_pages: 'Scrape', download_file: 'Download',
                          create_csv_file: 'Create CSV', create_chart: 'Create Chart',
                        };
                        return labels[t] || t;
                      }).join(' \u2192 ')}
                    </span>
                  </div>
                )}

                {workflowDetails.sourceUrl && (
                  <div className="mb-2">
                    <span className="text-muted-foreground text-xs">Data Source: </span>
                    <a
                      href={workflowDetails.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-xs break-all hover:underline"
                    >
                      {workflowDetails.sourceUrl}
                    </a>
                  </div>
                )}

                {workflowDetails.chartCode && (
                  <div>
                    <button
                      onClick={() => setShowChartCode(!showChartCode)}
                      className="text-primary text-xs flex items-center gap-1 hover:underline"
                    >
                      {showChartCode ? '\u25BC' : '\u25B6'} Chart Code (Python)
                    </button>
                    {showChartCode && (
                      <div className="relative mt-2">
                        <button
                          onClick={() => navigator.clipboard.writeText(workflowDetails.chartCode)}
                          className="absolute top-2 right-2 px-2 py-1 text-xs bg-muted border border-border rounded text-muted-foreground hover:text-foreground z-10"
                        >
                          Copy
                        </button>
                        <pre className="p-3 bg-background border border-border rounded-lg overflow-auto max-h-96 text-xs leading-relaxed text-foreground/80">
                          {workflowDetails.chartCode}
                        </pre>
                      </div>
                    )}
                  </div>
                )}

                {workflowDetails.elapsedSeconds > 0 && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Completed in {workflowDetails.elapsedSeconds}s
                  </div>
                )}
              </div>
            )}

            {/* Loading indicator — only show when no ThinkingSteps and no system messages with content */}
            {loading && thinkingSteps.length === 0 && !messages.some(m => m.type === 'system' && (m.content || '').trim().length > 0) && (
              <div className="flex justify-start animate-fade-in-up">
                <div className="bg-card/80 border border-border/50 rounded-2xl p-5 backdrop-blur-sm max-w-3xl">
                  <div className="flex items-start space-x-3">
                    <img
                      src="/cubemate-logo.png"
                      alt="Cubemate"
                      className="flex-shrink-0 w-9 h-9 rounded-lg animate-spin"
                      style={{ animationDuration: '3s' }}
                    />
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm text-muted-foreground font-medium">
                          {statusMessage || 'Workflow is running'}
                        </span>
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gradient-to-r from-primary to-brand rounded-full animate-bounce shadow-sm" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 bg-gradient-to-r from-primary to-brand rounded-full animate-bounce shadow-sm" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-2 h-2 bg-gradient-to-r from-primary to-brand rounded-full animate-bounce shadow-sm" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Form - Sticky at bottom */}
          <div className="flex-shrink-0 p-4 relative z-10">
            <form onSubmit={handleSendMessage} className="relative">
              <div className="relative max-w-3xl mx-auto w-full">
                <div className="relative flex items-center bg-white dark:bg-card border border-border/30 rounded-full p-3 shadow-xl hover:shadow-2xl transition-all">
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      if (textareaRef.current) {
                        textareaRef.current.style.height = 'auto';
                        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (!loading && input.trim()) {
                          handleSendMessage(e as any);
                        }
                      }
                    }}
                    placeholder="Try: 'Download Iris dataset and create a scatter plot colored by species'"
                    disabled={loading}
                    rows={1}
                    className="flex-1 px-4 py-3 bg-transparent text-foreground placeholder-muted-foreground/60 focus:outline-none resize-none min-h-[40px] max-h-[120px] overflow-y-auto text-[15px]"
                  />
                  <button
                    type="submit"
                    disabled={loading || !input.trim() || !currentSessionId}
                    className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-[#8E3BCE] to-[#6D2D8D] text-white rounded-full hover:shadow-lg hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                  >
                    {loading ? (
                      <span className="text-lg">&#8987;</span>
                    ) : (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
          </div>
        )}
        {/* End of Conditional (Empty vs With Messages) */}
        </div>
        {/* End of Chat Container div */}
      </div>
      {/* End of flex-1 main content div */}

      {/* Workspace Modal Overlay */}
      {workspaceModalOpen && workspaceContent && (
        <div className="fixed inset-0 z-50 bg-background">
          {/* Header with Close Button */}
          <div className="h-14 bg-card border-b border-border flex items-center justify-between px-6 shadow-lg">
            <div className="flex items-center space-x-3">
              <img src="/cubemate-logo.png" alt="Cubemate" className="w-8 h-8 rounded-lg" />
              <h3 className="text-lg font-bold text-foreground">
                &#9654;&#65039; Code Execution Workspace
              </h3>
            </div>
            <button
              onClick={() => setWorkspaceModalOpen(false)}
              className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors font-medium text-foreground flex items-center space-x-2 shadow-sm hover:shadow-md"
            >
              <span>&#10005;</span>
              <span>Close Workspace</span>
            </button>
          </div>

          {/* Workspace Content */}
          <div className="h-[calc(100vh-3.5rem)] overflow-auto">
            <CodeDisplay
              codeBlocks={workspaceContent.codeBlocks}
              currentMessageId={workspaceContent.messageId}
              sessionId={currentSessionId || undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default NewWorkflowPage;
